import { useQuery } from "@tanstack/react-query";
import { api } from "../../../../singleton/axios";

/**
 * Individual intrinsic value scenario
 */
export interface IntrinsicValueScenario {
  intrinsicValue: number;
  label: string;
}

/**
 * Warning about valuation limitations
 */
export interface IntrinsicValueWarning {
  code: "adr_no_coverage" | "limited_data";
  message: string;
}

/**
 * Response from /intrinsic-value endpoint
 */
export interface IntrinsicValueData {
  symbol: string;
  stockPrice: number;
  scenarios: {
    worstCase: IntrinsicValueScenario;
    baseCase: IntrinsicValueScenario;
    bestCase: IntrinsicValueScenario;
  };
  metadata: {
    dataSource: "blended" | "dcf" | "relative";
    calculatedAt: string;
    methodsUsed?: string[];
    isAdr?: boolean;
    warning?: IntrinsicValueWarning;
  };
}

export type ScenarioType = "worstCase" | "baseCase" | "bestCase";

/**
 * Hook to fetch blended intrinsic value scenarios (worst/base/best case)
 * Combines DCF and relative valuation methods (P/E, EV/EBITDA, Analyst targets)
 */
export const useIntrinsicValue = (symbol: string | undefined) => {
  return useQuery<IntrinsicValueData, Error>({
    queryKey: ["intrinsicValue", symbol],
    queryFn: async () => {
      if (!symbol) throw new Error("Symbol is required");
      const { data } = await api.get<IntrinsicValueData>("/intrinsic-value", {
        params: { symbol },
      });
      return data;
    },
    enabled: !!symbol,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
    retry: (failureCount, error) => {
      // Don't retry on 404s
      if ((error as any)?.response?.status === 404) return false;
      return failureCount < 2;
    },
  });
};
